import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { Header } from '@sorare/core/src/components/seo/Header';
import { OfficialProduct } from '@sorare/core/src/components/seo/OfficialProduct';
import {
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_LEAGUES_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { SeasonStandings } from '#src/pages/seo/SeasonStandings/index.tsx';

import { TopPlayers } from './TopPlayers/index.tsx';
import type {
  FootballLeagueOverviewQuery,
  FootballLeagueOverviewQueryVariables,
} from './__generated__/page.graphql.ts';

const FOOTBALL_LEAGUE_OVERVIEW_QUERY = gql`
  query FootballLeagueOverviewQuery($slug: String!) {
    football {
      competition(slug: $slug) {
        slug
        displayName
        pictureUrl
        released
        country {
          slug
          name
        }
        seasons {
          id
          startYear
        }
        ...Header_subscription
      }
    }
  }
  ${Header.fragments.subscription}
` as TypedDocumentNode<
  FootballLeagueOverviewQuery,
  FootballLeagueOverviewQueryVariables
>;

const Overview = () => {
  const { slug } = useParams();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');

  const { data, loading } = useQuery(FOOTBALL_LEAGUE_OVERVIEW_QUERY, {
    variables: { slug: slug || '' },
    skip: !slug,
  });

  useTitleAndDescription(
    metadatas.leagueOverview.title,
    metadatas.leagueOverview.description,
    {
      league: data?.football.competition.displayName,
    },
    {
      img: data?.football.competition.pictureUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { competition } = data.football;

  const blocks = {
    header: (
      <>
        <Header
          id={competition.slug}
          variant="team"
          displayName={competition.displayName}
          pictureUrl={competition.pictureUrl || defaultShield}
          memberships={
            competition.country
              ? [
                  {
                    name: competition.country.name,
                    pictureUrl: `${FRONTEND_ASSET_HOST}/flags/${competition.country.slug}.svg`,
                    to: generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE, {
                      countryCode: competition.country.slug,
                    }),
                  },
                ]
              : []
          }
          subscription={competition}
        />
        <Helmet
          script={[
            {
              type: 'application/ld+json',
              innerHTML: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'SportsOrganization',
                name: competition.displayName,
                mainEntityOfPage: generatePath(FOOTBALL_LEAGUES_SLUG, {
                  slug: competition.slug,
                }),
                sport: Sport.FOOTBALL,
                image: competition.pictureUrl,
              }),
            },
          ]}
        />
      </>
    ),
    topPlayers: <TopPlayers competitionSlug={competition.slug} />,
    seasonStandings: (
      <SeasonStandings
        displayName={competition.displayName}
        competitionSlug={competition.slug}
        seasons={competition.seasons}
      />
    ),
    officialProduct: competition.released && (
      <OfficialProduct
        displayName={competition.displayName}
        pictureUrl={competition.pictureUrl}
        to={generatePath(FOOTBALL_LEAGUES_SLUG, { slug: competition.slug })}
      />
    ),
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[blocks.topPlayers, blocks.officialProduct]}
      >
        {blocks.seasonStandings}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.seasonStandings}
      {blocks.topPlayers}
      {blocks.officialProduct}
    </Section>
  );
};

export default Overview;
